'use client';

import { useState, useEffect, useRef } from 'react';
import { TickerItem } from '../../types';

interface UseBinanceWebsocketProps {
  symbols: string[];
  onTickerUpdate?: (tickers: TickerItem[]) => void;
}

/**
 * Custom hook for connecting to Binance WebSocket API and receiving real-time ticker updates
 * 
 * @param symbols - Array of symbols to subscribe to (without USDT suffix)
 * @param onTickerUpdate - Optional callback function that will be called when new ticker data is received
 */
export function useBinanceWebsocket({
  symbols,
  onTickerUpdate
}: UseBinanceWebsocketProps) {
  const [tickers, setTickers] = useState<TickerItem[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // Add ref to track previous symbols to prevent unnecessary reconnections
  const previousSymbolsRef = useRef<string>('');
  
  // Use ref for callback to avoid dependency issues
  const onTickerUpdateRef = useRef(onTickerUpdate);
  onTickerUpdateRef.current = onTickerUpdate;
  
  useEffect(() => {
    // Don't connect if no symbols are provided
    if (!symbols || symbols.length === 0) return;

    // Check if symbols actually changed to prevent unnecessary reconnections
    const symbolsKey = [...symbols].sort().join(',');
    if (symbolsKey === previousSymbolsRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return; // No change in symbols and connection is open, skip reconnection
    }
    previousSymbolsRef.current = symbolsKey;

    let isSubscribed = true;
    const symbolsSet = new Set(symbols.map(s => s.toUpperCase() + 'USDT'));

    // Close any existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Connect to Binance WebSocket for all tickers
    const ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    ws.onclose = () => {
      if (isSubscribed) {
        setIsConnected(false);
      }
    };

    ws.onerror = (event) => {
      if (isSubscribed) {
        setError('WebSocket connection error');
        console.error('WebSocket error:', event);
      }
    };

    ws.onmessage = (event) => {
      if (!isSubscribed) return;
      
      try {
        const data = JSON.parse(event.data);
        
        // Filter for our subscribed symbols
        const relevantUpdates = data
          .filter((item: any) => symbolsSet.has(item.s))
          .map((item: any) => ({
            symbol: item.s, // Full symbol with USDT
            originalSymbol: item.s, // Keep original format for compatibility
            exchange: 'binance',
            price: parseFloat(item.c), // Current price
            priceChangePercent: parseFloat(item.P), // Price change percent
            volume: parseFloat(item.q) // Quote volume (in USDT)
          }));

        if (relevantUpdates.length > 0) {
          setTickers(relevantUpdates);
          
          // Call the callback if provided using ref
          if (onTickerUpdateRef.current) {
            onTickerUpdateRef.current(relevantUpdates);
          }
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err);
      }
    };

    // Cleanup function
    return () => {
      isSubscribed = false;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [symbols]); // Remove onTickerUpdate from dependencies to prevent reconnections

  return { tickers, isConnected, error };
}